import { Eye, EyeOff } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import type { ZXCVBNResult } from 'zxcvbn';

import { Button } from '@/components/ui/button';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { MINIMUM_PASSWORD_STRENGTH } from '../settings.schemas';
import { PasswordStrengthMeter } from './PasswordStrengthMeter';

type Zxcvbn = typeof import('zxcvbn');

/** zxcvbn is ~400KB, so it is fetched the first time a password is typed. */
let zxcvbnModule: Promise<Zxcvbn> | null = null;
function loadZxcvbn(): Promise<Zxcvbn> {
  zxcvbnModule ??= import('zxcvbn').then((m) => m.default ?? m);
  return zxcvbnModule;
}

/**
 * Live zxcvbn result for `password`, plus a submit-time check that resolves
 * to `true` when the password is strong enough for the auth service.
 */
export function usePasswordStrength(password: string) {
  const [strength, setStrength] = useState<ZXCVBNResult | null>(null);
  const latestPassword = useRef('');

  useEffect(() => {
    latestPassword.current = password;
    if (!password) {
      setStrength(null);
      return;
    }
    void loadZxcvbn().then((zxcvbn) => {
      // Ignore results for a value the user has already typed past.
      if (latestPassword.current === password) {
        setStrength(zxcvbn(password));
      }
    });
  }, [password]);

  const isStrongEnough = async (value: string): Promise<boolean> => {
    const zxcvbn = await loadZxcvbn();
    const result = zxcvbn(value);
    setStrength(result);
    return result.score >= MINIMUM_PASSWORD_STRENGTH;
  };

  const reset = () => setStrength(null);

  return { strength, isStrongEnough, reset };
}

type PasswordValues = { password: string; repeatPassword: string };

interface PasswordFieldsProps<T extends PasswordValues & FieldValues> {
  form: UseFormReturn<T>;
  strength: ZXCVBNResult | null;
  label?: string;
  repeatLabel?: string;
}

/**
 * Password + repeat inputs with a show/hide toggle and the strength meter.
 * Field names are fixed (`password`, `repeatPassword`); extend
 * `passwordFields` from the settings schemas to build the form schema.
 */
export function PasswordFields<T extends PasswordValues & FieldValues>({
  form,
  strength,
  label = 'New password',
  repeatLabel = 'Repeat new password',
}: PasswordFieldsProps<T>) {
  const [showPassword, setShowPassword] = useState(false);
  return (
    <>
      <FormField
        control={form.control}
        name={'password' as Path<T>}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{label}</FormLabel>
            <div className="relative w-full">
              <FormControl>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder={label}
                  className="pr-10"
                  {...field}
                />
              </FormControl>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute top-0 right-0 h-full px-3"
                onClick={() => setShowPassword((show) => !show)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="size-4" />
                ) : (
                  <Eye className="size-4" />
                )}
              </Button>
            </div>
            <PasswordStrengthMeter result={strength} />
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name={'repeatPassword' as Path<T>}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{repeatLabel}</FormLabel>
            <FormControl>
              <Input
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder={repeatLabel}
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
}
